import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  BadgeCheck,
  ScanBarcode,
  Package, 
  Tag,
  Box,
  Mail,
  BarChart3,
  ClipboardList,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  ShoppingCart,
  Building2,
  Layers,
  FileCheck,
  ClipboardCheck,
  TableProperties,
  Calculator,
  TrendingUp,
  History,
  Shirt,
  ListTodo,
  Users,
  Scissors,
  Menu,
  X,
  ShieldCheck,
  Truck,
  Megaphone,
  Warehouse,
  UserPlus,
  Palette,
  Sparkles,
  ShoppingBag,
  Image as ImageIcon,
  IndianRupee,
  ArrowLeftRight,
  Send,
  DollarSign,
  RefreshCw,
  Calendar,
  Wallet,
  Phone,
  FileSpreadsheet,
  HardDrive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { clearCache } from "@/utils/apiCache";

// Drive Module Navigation
const driveItems = [
  { path: "/drive", label: "All Files", icon: HardDrive, end: true },
];

// PackFlow Module Navigation
const packFlowItems = [
  { path: "/packflow", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/packflow/purchase-orders", label: "Purchase Orders", icon: FileText },
  { path: "/packflow/packing-lists", label: "Packing Lists", icon: Package },
  { path: "/packflow/sample-inventory", label: "Sample Inventory", icon: Box },
  { path: "/packflow/inventory", label: "Inventory", icon: Box },
  { path: "/packflow/packing-view", label: "Inspection View", icon: ClipboardList },
  { path: "/packflow/buyer-packing-list", label: "Buyer Packing List", icon: FileText },
  { path: "/packflow/packing-list-babyhug", label: "Packing List Babyhug", icon: Package },
  { path: "/packflow/progress", label: "Packing Progress", icon: BarChart3 },
  { path: "/packflow/scanning", label: "Scanning Station", icon: ScanBarcode },
  { path: "/packflow/labels", label: "Carton Labels", icon: Tag },
  { path: "/packflow/dispatch", label: "Dispatch Tracking", icon: Truck },
  { path: "/packflow/email-settings", label: "Email Settings", icon: Mail },
];

// Purchase Module Navigation
const purchaseItems = [
  { path: "/purchase/style-master", label: "Style Master", icon: Shirt },
  { path: "/purchase/buyer-orders", label: "Buyer Orders", icon: ShoppingCart },
  { path: "/purchase/po-consolidation", label: "PO Consolidation", icon: FileSpreadsheet },
  { path: "/purchase/buyer-orders-table", label: "Orders Table View", icon: TableProperties },
  { path: "/purchase/buyer-order-value", label: "Buyer Order Value", icon: DollarSign },
  { path: "/purchase/bom", label: "Bill of Materials", icon: Layers },
  { path: "/purchase/bom-tracking", label: "BOM Tracking", icon: ClipboardCheck },
  { path: "/purchase/vendors", label: "Vendors", icon: Building2 },
  { path: "/purchase/proposed-vendors", label: "Proposed Vendors", icon: UserPlus },
  { path: "/purchase/vendor-performance", label: "Vendor Performance", icon: TrendingUp },
  { path: "/purchase/price-history", label: "Price History", icon: History },
  { path: "/purchase/raw-material-po", label: "Raw Material PO", icon: FileCheck },
  { path: "/purchase/lab-dip-rate-card", label: "Lab Dip / Rate Card", icon: Palette },
  { path: "/purchase/buyer-approval-cards", label: "Buyer Approval Card", icon: BadgeCheck },
  { path: "/purchase/vendor-follow-up", label: "Vendor Follow-up", icon: Phone },
  { path: "/purchase/courier-tracking", label: "Courier Tracking", icon: Truck },
  { path: "/purchase/outward-challans", label: "Outward Challans", icon: Send },
  { path: "/purchase/miscellaneous-po", label: "Miscellaneous PO", icon: Package },
  { path: "/purchase/grn", label: "GRN Report", icon: ClipboardCheck },
  { path: "/purchase/return-challans", label: "Return Challans", icon: ArrowLeftRight },
  { path: "/purchase/production-plan", label: "Production Plan", icon: LayoutDashboard },
  { path: "/purchase/costing", label: "Costing", icon: Calculator },
  { path: "/purchase/sample-status-report", label: "Sample Status", icon: ClipboardCheck },
  { path: "/purchase/style-tna", label: "Style TNA", icon: BarChart3 },
  { path: "/purchase/tna-report", label: "TNA Report", icon: Calendar },
  { path: "/purchase/reports", label: "Purchase Reports", icon: BarChart3 },
];

// Delegation Module Navigation
const delegationItems = [
  { path: "/delegation", label: "Task Manager", icon: ListTodo, end: true },
];

// Marketing Module Navigation
const marketingItems = [
  { path: "/marketing", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/marketing/current-buyers", label: "Current Buyers", icon: Users },
  { path: "/marketing/prospective-buyers", label: "Prospective Buyers", icon: Users },
  { path: "/marketing/company-profile", label: "Company Profile", icon: Building2 },
];

// Accounts Module Navigation
const accountsItems = [
  { path: "/accounts", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/accounts/checklist", label: "Checklist", icon: ListTodo },
  { path: "/accounts/daily-expenses", label: "Daily Expenses", icon: Wallet },
  { path: "/accounts/wages-expenses", label: "Wages Expenses", icon: IndianRupee },
  { path: "/accounts/salary", label: "Salary", icon: IndianRupee },
  { path: "/accounts/bills", label: "Bills", icon: FileText },
];

// Cutting Module Navigation
const cuttingItems = [
  { path: "/cutting", label: "Issue & Receive", icon: Scissors, end: true },
  { path: "/cutting/flow", label: "Production Flow", icon: Package },
  { path: "/cutting/orders", label: "Cutting Orders", icon: ClipboardList },
  { path: "/cutting/payments", label: "Payments", icon: IndianRupee },
];

// Quality Review Module Navigation
const qualityItems = [
  { path: "/quality", label: "Quality Reviews", icon: ShieldCheck, end: true },
];

// Sampling Module Navigation
const samplingItems = [
  { path: "/sampling", label: "Samples", icon: Shirt, end: true },
  { path: "/sampling/stitch-plan", label: "Sample Stitch Plan", icon: Scissors },
  { path: "/sampling/design-library", label: "Design Library", icon: Palette },
  { path: "/sampling/costing", label: "Sample Costing", icon: Calculator },
];

// Productivity Module Navigation (formerly Unit Two)
const productivityItems = [
  { path: "/productivity", label: "Dashboard", icon: TrendingUp, end: true },
];

// Worker Scanner Module Navigation
const workerScannerItems = [
  { path: "/worker-scanner", label: "Worker Scanner", icon: ScanBarcode, end: true },
];

// Fabric Store Module Navigation
const storeItems = [
  { path: "/store", label: "Store Inventory", icon: Warehouse, end: true },
  { path: "/store/issue-receive", label: "Issue & Receive", icon: ArrowLeftRight },
  { path: "/store/style-tracker", label: "Style Tracker", icon: Layers },
  { path: "/store/reports", label: "Reports", icon: BarChart3 },
];

// Kidsdew Module Navigation
const kidsdewItems = [
  { path: "/kidsdew", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/kidsdew/products", label: "Products", icon: Package },
  { path: "/kidsdew/content", label: "Content Library", icon: ImageIcon },
  { path: "/kidsdew/posts", label: "Post Creator", icon: Sparkles },
  { path: "/kidsdew/orders", label: "Sales Hub", icon: ShoppingBag },
  { path: "/kidsdew/buyers", label: "Buyers", icon: Users },
  { path: "/kidsdew/reconciliation", label: "Reconciliation", icon: Calculator },
  { path: "/kidsdew/uniware", label: "Uniware Sync", icon: Warehouse },
  { path: "/kidsdew/dispatch", label: "Dispatch Tracking", icon: Truck },
];

const buyerSOPItems = [
  { path: "/buyer-sop", label: "Buyer SOP", icon: ClipboardList, end: true },
];

export default function Layout({ onLogout, moduleType = "packflow" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const username = localStorage.getItem("username") || "User";
  
  // Determine which items to show based on module type
  const navItems = moduleType === "purchase" 
    ? purchaseItems 
    : moduleType === "delegation" 
      ? delegationItems 
      : moduleType === "marketing"
        ? marketingItems
        : moduleType === "accounts"
          ? accountsItems
          : moduleType === "store"
            ? storeItems
            : moduleType === "cutting"
              ? cuttingItems
              : moduleType === "quality"
                ? qualityItems
                : moduleType === "sampling"
                  ? samplingItems
                  : moduleType === "productivity" || moduleType === "unit-two"
                    ? productivityItems
                    : moduleType === "worker-scanner"
                      ? workerScannerItems
                    : moduleType === "kidsdew"
                      ? kidsdewItems
                      : moduleType === "buyer-sop"
                        ? buyerSOPItems
                        : moduleType === "drive"
                          ? driveItems
                          : packFlowItems;
  
  const moduleInfo = moduleType === "purchase" 
    ? { name: "Purchase", icon: ShoppingCart, color: "text-green-600", bgColor: "bg-green-600" }
    : moduleType === "delegation"
      ? { name: "Delegation", icon: ListTodo, color: "text-purple-600", bgColor: "bg-purple-600" }
      : moduleType === "marketing"
        ? { name: "Marketing", icon: Users, color: "text-rose-600", bgColor: "bg-rose-600" }
        : moduleType === "accounts"
          ? { name: "Accounts", icon: ListTodo, color: "text-cyan-600", bgColor: "bg-cyan-600" }
          : moduleType === "store"
            ? { name: "Fabric Store", icon: Warehouse, color: "text-amber-600", bgColor: "bg-amber-600" }
            : moduleType === "kidsdew"
              ? { name: "Kidsdew", icon: ShoppingBag, color: "text-pink-600", bgColor: "bg-pink-600" }
              : moduleType === "cutting"
              ? { name: "Cutting", icon: Scissors, color: "text-orange-600", bgColor: "bg-orange-600" }
              : moduleType === "quality"
                ? { name: "Quality", icon: ShieldCheck, color: "text-teal-600", bgColor: "bg-teal-600" }
                : moduleType === "sampling"
                  ? { name: "Sampling", icon: Shirt, color: "text-pink-600", bgColor: "bg-pink-600" }
                  : moduleType === "productivity" || moduleType === "unit-two"
                    ? { name: "Productivity", icon: TrendingUp, color: "text-indigo-600", bgColor: "bg-indigo-600" }
                    : moduleType === "worker-scanner"
                      ? { name: "Worker Scanner", icon: ScanBarcode, color: "text-indigo-600", bgColor: "bg-indigo-600" }
                    : moduleType === "buyer-sop"
                      ? { name: "Buyer SOP", icon: ClipboardList, color: "text-indigo-600", bgColor: "bg-indigo-600" }
                      : moduleType === "drive"
                        ? { name: "Drive", icon: HardDrive, color: "text-violet-600", bgColor: "bg-violet-600" }
              : { name: "PackFlow", icon: Package, color: "text-blue-600", bgColor: "bg-blue-600" };
  
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    // The onLogout in App.js will handle the redirect via window.location.href
  };

  const handleRefresh = () => {
    // Clear API cache to ensure fresh data
    clearCache();
    // Reload the current page to fetch fresh data
    window.location.reload();
  };

  const handleSwitchModule = () => {
    navigate("/");
  };

  // Close sidebar when navigating on mobile
  const handleNavClick = () => {
    setSidebarOpen(false);
  };
  
  return (
    <div className="flex min-h-screen bg-white" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-zinc-200 z-40 flex items-center justify-between px-4">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 -ml-2 rounded-lg hover:bg-zinc-100"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex items-center ml-2">
            <div className={`w-7 h-7 ${moduleInfo.bgColor} rounded-lg flex items-center justify-center mr-2`}>
              <moduleInfo.icon className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-base">{moduleInfo.name}</span>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600"
          title="Refresh Data"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 border-r border-zinc-200 bg-zinc-50/50 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:transform-none
      `}>
        {/* Module Header */}
        <div className="h-16 flex items-center px-6 border-b border-zinc-200">
          <div className={`w-8 h-8 ${moduleInfo.bgColor} rounded-lg flex items-center justify-center mr-3`}>
            <moduleInfo.icon className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {moduleInfo.name}
            </span>
            <p className="text-xs text-zinc-400">Mini ERP</p>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto" data-testid="main-navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              data-testid={`nav-item-${item.path.replace(/^\//, "").replace(/\//g, "-")}`}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Switch Module Button */}
        <div className="p-4 border-t border-zinc-200 space-y-2">
          <button
            onClick={handleRefresh}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </button>
          <button
            onClick={() => { handleSwitchModule(); handleNavClick(); }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <Box className="w-4 h-4" />
            Switch Module
          </button>
        </div>

        {/* User & Logout */}
        <div className="p-4 border-t border-zinc-200">
          <div className="flex items-center gap-2 mb-3 text-sm text-zinc-600">
            <User className="w-4 h-4" />
            <span className="font-medium truncate">{username}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="w-full text-zinc-600 hover:text-red-600 hover:border-red-200"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-200 text-xs text-zinc-500">
          <p className="font-mono">Mini ERP System</p>
          <p className="mt-1">v2.0.0</p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
